import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import { useAuth } from '../../context/AuthContext';
import {
  PARTNER_CATEGORY_LABELS,
  PARTNER_TIER_LABELS,
  PROSPECT_PIPELINE,
  PROSPECT_SOURCE_LABELS,
  PROSPECT_STATUSES,
  PROSPECT_STATUS_LABELS,
  PROSPECT_STATUS_TONES,
  ProspectStatus,
} from '../../constants/partnerNetwork';
import {
  ActivityEntry,
  ConvertResult,
  Prospect,
  addProspectNote,
  convertProspect,
  getProspect,
  logProspectActivity,
  updateFollowUp,
  updateProspectStatus,
} from '../../services/partnerNetwork';
import {
  formatDate,
  formatDateTime,
  isOverdue,
  relativeDays,
  toDateInput,
} from '../../utils/partnerFormat';
import { defaultOutreachMessage, whatsappUrl } from '../../utils/whatsapp';
import BulkEmailModal from './BulkEmailModal';
import ProspectForm from './ProspectForm';
import {
  Alert,
  Modal,
  Pipeline,
  Spinner,
  StatusBadge,
  inputClass,
} from './ui';

/** Timeline icons, one per activity family. */
const ACTIVITY_ICONS: Record<string, string> = {
  prospect_created: 'M12 4v16m8-8H4',
  prospect_updated: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  status_changed: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  email_sent: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  email_failed: 'M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  whatsapp_opened: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  follow_up_scheduled: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  note_added: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  partner_onboarded: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
};

const FALLBACK_ICON = 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';

const ProspectDetail: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { admin } = useAuth();

  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [canEdit, setCanEdit] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  const [statusDraft, setStatusDraft] = useState<ProspectStatus>('prospect');
  const [statusNote, setStatusNote] = useState('');
  const [followUpDraft, setFollowUpDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [convertResult, setConvertResult] = useState<ConvertResult | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getProspect(id);
      setProspect(response.prospect);
      setActivities(response.activities);
      setCanEdit(response.canEdit);
      setStatusDraft(response.prospect.status);
      setFollowUpDraft(toDateInput(response.prospect.nextFollowUpAt));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the prospect');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  /* --- actions --- */

  const run = async (action: () => Promise<void>, success?: string) => {
    setError(null);
    setNotice(null);
    try {
      setBusy(true);
      await action();
      if (success) setNotice(success);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const saveStatus = () =>
    run(async () => {
      const updated = await updateProspectStatus(id, statusDraft, {
        note: statusNote.trim() || undefined,
      });
      setProspect(updated);
      setStatusNote('');
      setStatusOpen(false);
      await load();
    }, 'Status updated.');

  const saveFollowUp = () =>
    run(async () => {
      const updated = await updateFollowUp(id, {
        nextFollowUpAt: followUpDraft || null,
      });
      setProspect(updated);
      await load();
    }, followUpDraft ? 'Follow-up set.' : 'Follow-up cleared.');

  const saveNote = () =>
    run(async () => {
      if (!noteDraft.trim()) return;
      const updated = await addProspectNote(id, noteDraft.trim());
      setProspect(updated);
      setNoteDraft('');
      await load();
    }, 'Note added.');

  const openWhatsApp = async () => {
    if (!prospect) return;

    const url = whatsappUrl(
      prospect.phone,
      defaultOutreachMessage({
        name: prospect.fullName,
        companyName: prospect.companyName,
        senderName: admin?.name,
      })
    );

    if (!url) {
      setError('This prospect has no usable phone number.');
      return;
    }

    // Opened inside the click so the browser does not treat it as a popup.
    window.open(url, '_blank', 'noopener,noreferrer');

    try {
      await logProspectActivity(id, {
        type: 'whatsapp_opened',
        description: `WhatsApp opened — ${prospect.phone}`,
        markContacted: true,
      });
      await load();
    } catch {
      // The chat is already open; a missing timeline row is not worth an alert.
    }
  };

  const handleConvert = () =>
    run(async () => {
      const result = await convertProspect(id);
      setConvertResult(result);
      await load();
    });

  /* --- render --- */

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  if (!prospect) {
    return (
      <section className="py-12">
        <div className="mx-auto max-w-3xl px-4">
          <Alert tone="error">{error || 'Prospect not found.'}</Alert>
          <Link to="/admin/partners/prospects" className="btn-secondary mt-6 py-2.5 px-5">
            Back to prospects
          </Link>
        </div>
      </section>
    );
  }

  const offPipeline =
    prospect.status === 'nurture' || prospect.status === 'not_interested';

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/admin/partners/prospects"
          className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M15 19l-7-7 7-7" />
          </svg>
          All prospects
        </Link>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}
        {notice && !error && (
          <Alert tone="success" className="mb-6">
            {notice}
          </Alert>
        )}
        {!canEdit && (
          <Alert tone="info" className="mb-6">
            This prospect is assigned to {prospect.assignedMarketerName || 'another marketer'}.
            You can see the record, but not change it.
          </Alert>
        )}

        {/* Header */}
        <AnimatedSection animation="slide-up">
          <div className="mb-6 rounded-2xl border border-surface-border bg-surface-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <h1 className="font-display text-2xl font-semibold text-white">
                    {prospect.fullName}
                  </h1>
                  <StatusBadge
                    label={PROSPECT_STATUS_LABELS[prospect.status]}
                    tone={PROSPECT_STATUS_TONES[prospect.status]}
                  />
                </div>
                <p className="text-sm text-muted">
                  {[
                    prospect.jobTitle,
                    prospect.companyName,
                    prospect.category && PARTNER_CATEGORY_LABELS[prospect.category],
                    prospect.tier && PARTNER_TIER_LABELS[prospect.tier],
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'No company details yet'}
                </p>
                <p className="mt-1 text-xs text-muted/70">
                  {prospect.prospectId} · added {formatDate(prospect.createdAt)}
                  {prospect.source && ` · via ${PROSPECT_SOURCE_LABELS[prospect.source]}`}
                </p>
              </div>

              {canEdit && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setEmailOpen(true)}
                    disabled={!prospect.email}
                    className="btn-secondary py-2.5 px-4 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={openWhatsApp}
                    disabled={!prospect.phone}
                    className="btn-secondary py-2.5 px-4 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditOpen(true)}
                    className="btn-secondary py-2.5 px-4 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatusOpen(true)}
                    className="btn-secondary py-2.5 px-4 text-sm"
                  >
                    Change status
                  </button>
                  {prospect.status === 'onboarding' && !prospect.convertedPartnerId && (
                    <button
                      type="button"
                      onClick={() => setConvertOpen(true)}
                      className="btn-primary py-2.5 px-4 text-sm"
                    >
                      Convert to partner
                    </button>
                  )}
                </div>
              )}
            </div>

            {prospect.convertedPartnerId && (
              <div className="mt-5 border-t border-surface-border pt-5">
                <Alert tone="success">
                  Converted to a partner on {formatDate(prospect.convertedAt)}.{' '}
                  <Link
                    to={`/admin/partners/partners/${prospect.convertedPartnerId}`}
                    className="underline underline-offset-2"
                  >
                    Open the partner record
                  </Link>
                  .
                </Alert>
              </div>
            )}
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {/* Pipeline */}
            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-muted">
                Pipeline
              </h2>
              <Pipeline
                stages={PROSPECT_PIPELINE}
                labels={PROSPECT_STATUS_LABELS}
                current={prospect.status}
                offTrackNote={
                  offPipeline
                    ? `Currently marked ${PROSPECT_STATUS_LABELS[
                        prospect.status
                      ].toLowerCase()} — off the acquisition track.`
                    : undefined
                }
              />
            </div>

            {/* Notes */}
            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Notes
              </h2>

              {canEdit && (
                <div className="mb-5">
                  <textarea
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    rows={3}
                    placeholder="What came out of the conversation?"
                    className={`${inputClass} resize-y`}
                  />
                  <button
                    type="button"
                    onClick={saveNote}
                    disabled={busy || !noteDraft.trim()}
                    className="btn-primary mt-3 py-2 px-4 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Add note
                  </button>
                </div>
              )}

              {prospect.notes.length === 0 ? (
                <p className="text-sm text-muted">No notes yet.</p>
              ) : (
                <ul className="space-y-3">
                  {[...prospect.notes].reverse().map((note) => (
                    <li
                      key={note.id}
                      className="rounded-lg border border-surface-border bg-surface p-4"
                    >
                      <p className="whitespace-pre-wrap text-sm text-white">{note.body}</p>
                      <p className="mt-2 text-xs text-muted">
                        {note.authorName} · {formatDateTime(note.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Timeline */}
            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-muted">
                Activity
              </h2>

              {activities.length === 0 ? (
                <p className="text-sm text-muted">Nothing recorded yet.</p>
              ) : (
                <ol className="space-y-0">
                  {activities.map((activity, index) => (
                    <li key={activity.id} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
                            activity.type === 'email_failed'
                              ? 'border-red-500/40 bg-red-500/10 text-red-400'
                              : 'border-surface-border bg-surface text-muted'
                          }`}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.7}
                              d={ACTIVITY_ICONS[activity.type] || FALLBACK_ICON}
                            />
                          </svg>
                        </span>
                        {index < activities.length - 1 && (
                          <span aria-hidden className="w-px flex-1 bg-surface-border" />
                        )}
                      </div>
                      <div className="min-w-0 pb-6">
                        <p className="text-sm text-white">{activity.description}</p>
                        <p className="mt-1 text-xs text-muted">
                          {activity.actorName} · {formatDateTime(activity.createdAt)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Contact
              </h2>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-muted">Email</dt>
                  <dd className="break-all text-white">
                    {prospect.email ? (
                      <a href={`mailto:${prospect.email}`} className="hover:text-primary-400">
                        {prospect.email}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted">Phone</dt>
                  <dd className="text-white">{prospect.phone || '—'}</dd>
                </div>
                <div>
                  <dt className="text-muted">Location</dt>
                  <dd className="text-white">
                    {[prospect.city, prospect.country].filter(Boolean).join(', ') || '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted">Website</dt>
                  <dd className="break-all text-white">
                    {prospect.website ? (
                      <a
                        href={prospect.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary-400"
                      >
                        {prospect.website}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted">LinkedIn</dt>
                  <dd className="break-all text-white">
                    {prospect.linkedinUrl ? (
                      <a
                        href={prospect.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary-400"
                      >
                        View profile
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted">Assigned to</dt>
                  <dd className="text-white">
                    {prospect.assignedMarketerName || 'Unassigned'}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Follow-up
              </h2>

              <dl className="mb-5 space-y-3 text-sm">
                <div>
                  <dt className="text-muted">Last contacted</dt>
                  <dd className="text-white">{formatDate(prospect.lastContactedAt)}</dd>
                </div>
                <div>
                  <dt className="text-muted">Next follow-up</dt>
                  <dd
                    className={
                      isOverdue(prospect.nextFollowUpAt) ? 'text-red-400' : 'text-white'
                    }
                  >
                    {prospect.nextFollowUpAt
                      ? `${formatDate(prospect.nextFollowUpAt)} — ${relativeDays(
                          prospect.nextFollowUpAt
                        )}`
                      : 'Not scheduled'}
                  </dd>
                </div>
              </dl>

              {canEdit && (
                <div className="space-y-3">
                  <input
                    type="date"
                    value={followUpDraft}
                    onChange={(e) => setFollowUpDraft(e.target.value)}
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={saveFollowUp}
                    disabled={busy}
                    className="btn-secondary w-full py-2.5 text-sm disabled:opacity-60"
                  >
                    {followUpDraft ? 'Set follow-up' : 'Clear follow-up'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProspectForm
        open={editOpen}
        prospect={prospect}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setNotice('Prospect updated.');
          load();
        }}
      />

      <BulkEmailModal
        open={emailOpen}
        prospects={[prospect]}
        onClose={() => setEmailOpen(false)}
        onSent={load}
      />

      <Modal
        open={statusOpen}
        title="Change status"
        description="Every change is recorded on the timeline."
        onClose={() => setStatusOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setStatusOpen(false)}
              className="btn-secondary py-2.5 px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveStatus}
              disabled={busy}
              className="btn-primary py-2.5 px-5 disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Save status'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm text-muted">Status</label>
            <select
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value as ProspectStatus)}
              className={inputClass}
            >
              {PROSPECT_STATUSES.map((value) => (
                <option
                  key={value}
                  value={value}
                  /* Reaching Active partner by hand would flag a partner with
                     no partner account behind it — that is what Convert does. */
                  disabled={value === 'active_partner' && !prospect.convertedPartnerId}
                >
                  {PROSPECT_STATUS_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm text-muted">Note (optional)</label>
            <textarea
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={3}
              placeholder="What moved this along?"
              className={`${inputClass} resize-y`}
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={convertOpen}
        title={convertResult ? 'Partner created' : 'Convert to partner'}
        onClose={() => {
          setConvertOpen(false);
          setConvertResult(null);
        }}
        footer={
          convertResult ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setConvertOpen(false);
                  setConvertResult(null);
                }}
                className="btn-secondary py-2.5 px-5"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() =>
                  navigate(`/admin/partners/partners/${convertResult.partner.id}`)
                }
                className="btn-primary py-2.5 px-5"
              >
                Open partner
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setConvertOpen(false)}
                className="btn-secondary py-2.5 px-5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConvert}
                disabled={busy}
                className="btn-primary py-2.5 px-5 disabled:opacity-60"
              >
                {busy ? 'Creating…' : 'Create partner account'}
              </button>
            </>
          )
        }
      >
        {convertResult ? (
          <div className="space-y-4">
            <Alert tone="success">
              {convertResult.adoptedExistingAccount
                ? `${prospect.fullName} already had a portal account — it has been approved and linked to this prospect.`
                : `${prospect.fullName} is now partner ${convertResult.partner.partnerId}.`}
            </Alert>

            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-muted">Partner ID</dt>
                <dd className="font-medium text-white">{convertResult.partner.partnerId}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted">Sign-in email</dt>
                <dd className="break-all text-white">{convertResult.partner.email}</dd>
              </div>
            </dl>

            {convertResult.temporaryPassword ? (
              <Alert tone="warning">
                <p className="font-medium">The welcome email did not send.</p>
                <p className="mt-1">
                  Pass these details on yourself — this password is shown once and is not
                  stored anywhere you can read it again.
                </p>
                <p className="mt-2 font-mono text-white">
                  {convertResult.partner.email} / {convertResult.temporaryPassword}
                </p>
              </Alert>
            ) : (
              <p className="text-sm text-muted">
                A welcome email with their Partner ID and sign-in link has gone out.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4 text-sm text-muted">
            <p>
              This creates a partner account for{' '}
              <span className="text-white">{prospect.fullName}</span>, issues a Partner ID,
              and emails them their portal sign-in.
            </p>
            <p>
              The prospect record and its whole history stay exactly as they are, linked to
              the new partner.
            </p>
            {!prospect.email && (
              <Alert tone="error">
                This prospect has no email address, so no login can be created. Add one first.
              </Alert>
            )}
          </div>
        )}
      </Modal>
    </section>
  );
};

export default ProspectDetail;
